"use client";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useRef, useLayoutEffect, useEffect } from "react";

export default function ExpandableSubheading({ subHeading }: { subHeading?: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const collapsedHeight = 40;
  const truncatHeight = 18;

  useLayoutEffect(() => {
    if (!contentRef.current) return;

    const updateHeight = () => {
      if (contentRef.current) {
        const height = contentRef.current.scrollHeight;
        if (height <= truncatHeight) return setNeedsTruncation(false);
        setNeedsTruncation(height > truncatHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, [subHeading, isLoading]);

  useEffect(() => {
    if (subHeading) {
      setIsLoading(false);
    }
  }, [subHeading]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={isExpanded ? "expanded" : "collapsed"}
        initial={{ height: needsTruncation ? collapsedHeight : "auto" }}
        animate={{ height: !needsTruncation ? "auto" : isExpanded ? "auto" : collapsedHeight }}
        exit={{ height: collapsedHeight }}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        className="overflow-hidden text-xs sm:text-sm text-white/60 mb-6"
      >
        {isLoading ? (
          <LoadingSatate />
        ) : (
          <>
            <p
              ref={contentRef}
              className={!isExpanded ? "line-clamp-1" : ""}
              dangerouslySetInnerHTML={{ __html: subHeading ?? "" }}
            />

            {needsTruncation && (
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                className={`text-white transition duration-300 font-bold`}
              >
                {isExpanded ? "Read Less" : "Read More"}
              </button>
            )}
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function LoadingSatate() {
  return (
    <div className="section-lg flex flex-col justify-between gap-1.5 w-full">
      <div className="h-4 sm:h-5.5 w-full mx-auto bg-[#b7b7b7] rounded-md animate-pulse" />
      <div className="h-4 sm:h-5.5 w-2/3 mx-auto bg-[#b7b7b7] rounded-md animate-pulse" />
      <div className="h-3 sm:hidden w-1/6 mx-auto bg-[#b7b7b7] rounded-md animate-pulse" />
    </div>
  );
}
